import { inject, Injectable } from '@angular/core';
import {
  BottomPanelMessageType,
  IBottomPanelButtonConfig
} from '@design-system/components/m-bottom-panel';
import { NoDataState } from '@design-system/components/m-not-available';
import { IFilterData } from '@design-system/components/m-text-field';
import { ICampaignsCount, IVCampaign } from '@libs/apis';
import { IPTableColumnInfo, ViewType } from '@libs/common-components';
import {
  camelCase,
  cloneDeep,
  isEmpty,
  isEqual,
  isNil,
  snakeCase,
  uniq,
  uniqBy
} from 'lodash-es';
import { MenuItem } from 'primeng/api';
import { Table } from 'primeng/table';
import { CampaignTableConstants } from '../components/campaign-table/campaign-table.constants';
import {
  Constants as CampaignsViewConstants,
  Constants
} from '../components/campaigns-view/campaigns-view.constants';
import { SlidingQueue } from '../helper/sliding-queue';
import { TaxonomyHelper } from '../helper/taxonomy-helper';
import {
  ICampaignFilterContext,
  TaxonomyDimension
} from '../models/ICampaignFilterContext';
import { IColumnFilterContext } from '../models/IColumnFilterContext';
import { TaxonomyFeature } from '../models/TaxonomyFeature';
import { TaxonomyTab } from '../models/TaxonomyTab';
import { TaxonomyUiConstants } from '../taxonomy-ui/taxonomy-ui.constants';
import { CampaignViewService } from './campaign-view.service';
import { CampaignsGlobalLoaderService } from './campaigns-global-loader.service';
import { TableDataService } from './table-data.service';

@Injectable({
  providedIn: 'root'
})
export class CampaignTableService {
  public columns: IPTableColumnInfo[] = [];
  public campaignDimensionInfo!: ICampaignsCount | null;
  public virtualPageSize = 200;
  public campaignQueue: SlidingQueue<IVCampaign> = new SlidingQueue<IVCampaign>(
    this.virtualPageSize
  );

  public showUpdateMappingOverlay = false;
  public viewFor = TaxonomyFeature.GA;
  public clientId = 0;
  public brandId = 0;
  public loading = false;
  public filterStateByTab: Record<string, IColumnFilterContext> = {};
  public pageNumber = 0;
  public selectedCampaign: IVCampaign | null = null;
  public activeTab!: TaxonomyTab;
  public requestInProgress = false;
  private pageSize = 200000;
  public filterContext!: ICampaignFilterContext;
  public timezone = 'America/New_York';
  public selectionInProgress = false;
  public sort: { sortBy: string; sortOrder: string } | null = null;
  private emptyFilterState = CampaignsViewConstants.defaultFilterState;
  public activeDimensionPageNumber = 0;
  public loadNext = CampaignsViewConstants.loadNextText;
  public loadPrevious = CampaignsViewConstants.loadPreviousText;
  public selectedCampaigns: IVCampaign[] = [];
  public undoSelectedCampaigns: IVCampaign[] = [];
  public allCampaignsSelected = false;
  public selectedCampaignForAction: IVCampaign | null = null;
  public showAiRecommendations = false;
  public noData = NoDataState.noData;
  public campaignTable!: Table;
  public isAiRecommendedDataRequested = false;
  public globalLoader: Record<string, boolean | string | ViewType | null> = {
    show: false,
    title: null,
    message: null,
    view: ViewType.empty
  };

  public reviewUndoMessage = {
    message: '',
    messageType: BottomPanelMessageType.base
  };

  public reviewNavigationButtons: IBottomPanelButtonConfig[] = [];
  public actionMenuItems: Record<string, MenuItem[]> =
    CampaignTableConstants.actionMenuItems;

  public actionStep: 'choice' | 'override' = 'choice';
  public geoTestStatusByDimension!: Record<string, boolean>;
  public actualColumns: IPTableColumnInfo[] = [];

  readonly tableDataService: TableDataService = inject(TableDataService);
  readonly campaignViewService: CampaignViewService =
    inject(CampaignViewService);

  readonly campaignsGlobalLoaderService: CampaignsGlobalLoaderService = inject(
    CampaignsGlobalLoaderService
  );

  get keyForActiveTabAndDimension(): string {
    return `${this.clientId}-${this.brandId}-${
      this.activeTab
    }-${snakeCase(this.campaignDimensionInfo?.dimension ?? '')}-${this.viewFor}-${this.isAiRecommendedDataRequested ? 'ai-recommended' : ''}`;
  }

  public getColumnFilterStateKey(activeAccordion?: string): string {
    return `${this.activeTab}-${activeAccordion ?? this.campaignDimensionInfo?.dimension ?? ''}-${this.isAiRecommendedDataRequested ? 'ai-recommended' : ''}`;
  }

  public initializeComponent(): void {
    this.filterStateByTab = {};
    this.campaignQueue.flush();
    this.allCampaignsSelected = false;
    this.selectionInProgress = false;
  }

  public getColumnFilterState(header: { field: string }): IFilterData {
    return (
      this.filterStateByTab[this.getColumnFilterStateKey()]?.filterCriteria[
        header.field
      ] ?? cloneDeep(this.emptyFilterState)
    );
  }

  public getDefaultColumnFilterState(
    activeAccordionDimensionValue: string
  ): IColumnFilterContext {
    return cloneDeep({
      filterCriteria: {},
      activeDimension: activeAccordionDimensionValue,
      activeTab: this.activeTab,
      totalCount: this.campaignDimensionInfo?.count || 0,
      filteredCount: 0
    });
  }

  public updateCampaignsWithFilter(
    dimensionKey: string,
    dimensionValue: IFilterData
  ): void {
    if (isNil(this.filterStateByTab[this.getColumnFilterStateKey()])) {
      this.filterStateByTab[this.getColumnFilterStateKey()] =
        this.getDefaultColumnFilterState(
          this.campaignDimensionInfo?.dimension as string
        );
    }

    const previousFilter =
      this.filterStateByTab[this.getColumnFilterStateKey()]?.filterCriteria[
        dimensionKey
      ];
    if (previousFilter === dimensionValue) {
      return;
    }
    (
      this.filterStateByTab[
        this.getColumnFilterStateKey()
      ] as IColumnFilterContext
    ).filterCriteria[dimensionKey] = dimensionValue;
    this.repopulateCampaignsFromIndexDB();
  }

  public handleCampaignSortChange(event: {
    sortBy: string;
    sortOrder: string;
  }): void {
    if (isEqual(this.sort, event)) {
      return;
    }
    this.sort = {
      sortBy: this.columns.find((header) => header.header === event.sortBy)
        ?.field as string,
      sortOrder: event.sortOrder
    };
    this.repopulateCampaignsFromIndexDB(true);
  }

  private repopulateCampaignsFromIndexDB(sorting = false): void {
    this.loading = true;
    this.activeDimensionPageNumber = 0;
    this.pageNumber = 0;
    this.campaignQueue.flush();

    const filterCriteria = TaxonomyHelper.prepareGetCampaignFilterCriteria(
      this.filterStateByTab[
        this.getColumnFilterStateKey()
      ] as IColumnFilterContext,
      this.selectedCampaign
    );

    const promises: Promise<unknown>[] = [
      this.tableDataService
        .getCampaignsWithFilter(this.keyForActiveTabAndDimension, {
          pageNumber: this.activeDimensionPageNumber,
          filterCriteria,
          ...(this.sort
            ? { sortBy: this.sort.sortBy, sortOrder: this.sort.sortOrder }
            : {})
        })
        .then(({ campaigns }) => {
          this.campaignQueue.addLast(campaigns);
          this.loading = false;
        })
        .catch((error) => {
          console.error('Error fetching campaigns from IndexedDB:', error);
          this.loading = false;
        })
    ];

    if (!sorting) {
      promises.push(
        this.tableDataService
          .getCampaignsCountWithFilter(this.keyForActiveTabAndDimension, {
            filterCriteria
          })
          .then((count: number) => {
            if (this.campaignDimensionInfo) {
              this.campaignDimensionInfo.count = count;
            }
          })
          .catch((error) => {
            console.error(
              'Error fetching campaign count from IndexedDB:',
              error
            );
          })
      );
    }

    Promise.all(promises).catch((error) => {
      console.error('Error in repopulateCampaignsFromIndexDB:', error);
      this.loading = false;
    });
  }

  public loadMoreCampaignsFromIndexDB(next?: boolean, table?: Table): void {
    if (table) {
      this.campaignTable = table;
    }
    if (!next) {
      if (this.activeDimensionPageNumber === 0) {
        return;
      }
      this.loadPrevious = CampaignsViewConstants.loadingText;
      this.activeDimensionPageNumber -= 1;
      this.getPreviousPageDataFromIndexDB();
    } else {
      this.loadNext = CampaignsViewConstants.loadingText;
      this.activeDimensionPageNumber += 1;
      this.getNextPageDataFromIndexDB();
    }
  }

  private getPreviousPageDataFromIndexDB(): void {
    this.tableDataService
      .getCampaignsWithFilter(this.keyForActiveTabAndDimension, {
        pageNumber: this.activeDimensionPageNumber
      })
      .then(({ campaigns }) => {
        this.requestInProgress = false;
        this.campaignQueue.addFirst(campaigns);
        this.loadPrevious = CampaignsViewConstants.loadPreviousText;
        this.campaignTable.scrollTo({ top: 640 });
      });
  }

  private getNextPageDataFromIndexDB(): void {
    this.tableDataService
      .getCampaignsWithFilter(this.keyForActiveTabAndDimension, {
        pageNumber: this.activeDimensionPageNumber
      })
      .then(({ campaigns }) => {
        this.requestInProgress = false;
        this.loadNext = CampaignsViewConstants.loadNextText;
        this.campaignQueue.addLast(campaigns);
      });
  }

  public async updateCampaignSelection(
    $event: { checked: boolean },
    campaign: IVCampaign
  ): Promise<void> {
    if (this.selectionInProgress) {
      return;
    }

    this.selectionInProgress = true;
    if ($event.checked) {
      this.tableDataService.selectCampaign(
        this.keyForActiveTabAndDimension,
        campaign.tempRecordId
      );

      this.campaignQueue.queue
        .filter(
          (campaignInQueue: IVCampaign) =>
            campaignInQueue.tempRecordId === campaign.tempRecordId
        )
        .forEach(
          (campaignInQueue: IVCampaign) => (campaignInQueue.isSelected = true)
        );

      this.selectedCampaigns.push(campaign);
      this.selectionInProgress = false;

      this.showUpdateMappingOverlay = true;
    } else {
      this.tableDataService.unselectCampaign(
        this.keyForActiveTabAndDimension,
        campaign.tempRecordId
      );

      this.campaignQueue.queue
        .filter(
          (campaignInQueue) =>
            campaignInQueue.tempRecordId === campaign.tempRecordId
        )
        .forEach((campaignInQueue) => (campaignInQueue.isSelected = false));

      const index = this.selectedCampaigns.indexOf(campaign);
      this.selectedCampaigns.splice(index, 1);
      this.selectionInProgress = false;

      this.showUpdateMappingOverlay =
        this.campaignQueue.queue.filter(
          (campaignInQueue) => campaignInQueue.isSelected
        ).length > 0;
    }

    this.selectedCampaigns = uniq(this.selectedCampaigns);
    this.allCampaignsSelected =
      this.selectedCampaigns.length > 0 &&
      this.selectedCampaigns.length ===
        (this.campaignDimensionInfo?.count || 0);
    if (this.selectedCampaigns.length === 0) {
      this.changeActionStep('choice');
    }
  }

  public clearUnmapCampaignAction(): void {
    setTimeout(() => {
      this.selectedCampaignForAction = null;
    }, CampaignTableConstants.delayForClearingSelectedCampaignInMillis);
  }

  public setUnmapCampaignAction(campaign: IVCampaign): void {
    this.selectedCampaignForAction = campaign;
  }

  public async selectAllCampaignsInDimension($event: {
    checked: boolean;
  }): Promise<void> {
    this.allCampaignsSelected = $event.checked;
    if (!$event.checked) {
      await this.tableDataService.unselectAll(this.keyForActiveTabAndDimension);
      this.changeActionStep('choice');
    }
  }

  public fetchAndPopulateCampaignsTable() {
    this.requestInProgress = true;
    this.tableDataService
      .getStore(this.keyForActiveTabAndDimension)
      .resetDatabase()
      .then(() => {
        this.campaignQueue.flush();
        this.fetchAndEnrichCampaigns();
      })
      .catch((error) => {
        console.error(
          'Error occured while fetching and populating campaigns table',
          error
        );
        this.loading = false;
        this.requestInProgress = false;
      });
  }

  public loadCampaignsWithLazyLoading(forceRefresh = false) {
    this.tableDataService
      .hasData(this.keyForActiveTabAndDimension)
      .then((hasData) => {
        if (hasData && !forceRefresh) {
          this.repopulateCampaignsTable();
        } else {
          this.tableDataService.createStore(this.keyForActiveTabAndDimension);
          this.fetchAndPopulateCampaignsTable();
        }
      })
      .catch((error) => {
        console.error(
          'Error occured while loading campaigns with lazy loading',
          error
        );
        this.loading = false;
        this.requestInProgress = false;
      });
  }

  private repopulateCampaignsTable() {
    this.requestInProgress = true;
    this.loading = true;
    this.tableDataService
      .getCampaignsWithFilter(this.keyForActiveTabAndDimension, {
        pageNumber: this.pageNumber,
        filterCriteria: TaxonomyHelper.prepareGetCampaignFilterCriteria(
          this.filterStateByTab[
            this.getColumnFilterStateKey()
          ] as IColumnFilterContext,
          this.selectedCampaign
        )
      })
      .then(({ campaigns }) => {
        this.campaignQueue.flush();
        this.campaignQueue.addLast([...campaigns]);
      })
      .finally(() => {
        this.loading = false;
        this.requestInProgress = false;
      });

    this.prepareCampaignTableColumnsByDimension().catch(console.error);
  }

  private getDimensionByActiveTab() {
    return this.activeTab === TaxonomyTab.underProcessing &&
      this.filterContext.groupByDimension !==
        TaxonomyDimension.compositeDataSourceName
      ? camelCase(`operational_${this.filterContext.groupByDimension}`)
      : this.filterContext.groupByDimension;
  }

  private filterOutMultipleTacticsCampaigns(campaigns: IVCampaign[]) {
    return campaigns.filter(
      (campaign) => campaign.tactic !== TaxonomyUiConstants.multipleTactics
    );
  }

  private enrichCampaignsWithMetadata(campaigns: IVCampaign[]) {
    const nonMultipleTacticCampaigns =
      this.filterOutMultipleTacticsCampaigns(campaigns);

    return nonMultipleTacticCampaigns.map((campaign) => {
      const enrichedCampaign = TaxonomyHelper.enrichCampaign(
        campaign,
        this.timezone
      );

      if (this.allCampaignsSelected) {
        enrichedCampaign.isSelected = true;
      }

      enrichedCampaign.dimensionKey = this.keyForActiveTabAndDimension;
      enrichedCampaign.geoTestStatus =
        this.geoTestStatusByDimension[
          campaign.operationalTactic || campaign.tactic
        ];

      return enrichedCampaign;
    });
  }

  private fetchAndEnrichCampaigns() {
    this.loading = true;

    const campaignRequest = {
      clientId: this.clientId,
      brandId: this.brandId,
      taxonomyTab: this.activeTab,
      dimensionValue: this.campaignDimensionInfo?.dimension,
      offset: 0,
      limit: this.pageSize,
      dateRange: this.filterContext.dateRange,
      groupByDimension: this.getDimensionByActiveTab(),
      isAiRecommended: this.isAiRecommendedDataRequested
    };

    const filterCriteria = TaxonomyHelper.prepareGetCampaignFilterCriteria(
      this.filterStateByTab[
        this.getColumnFilterStateKey()
      ] as IColumnFilterContext,
      this.selectedCampaign
    );

    Promise.all([
      this.campaignViewService.getCampaignsByDimension(
        campaignRequest.clientId,
        campaignRequest.brandId,
        campaignRequest.taxonomyTab,
        campaignRequest.dimensionValue as string,
        filterCriteria,
        campaignRequest.offset,
        campaignRequest.limit,
        campaignRequest.dateRange,
        campaignRequest.groupByDimension as TaxonomyDimension,
        campaignRequest.isAiRecommended
      ),
      this.prepareCampaignTableColumnsByDimension()
    ])
      .then(([campaigns]) => {
        const enrichedCampaigns = this.enrichCampaignsWithMetadata(campaigns);

        this.tableDataService
          .populateDataStore(
            this.keyForActiveTabAndDimension,
            enrichedCampaigns
          )
          .catch(console.error);

        this.tableDataService
          .getCampaignsWithFilter(this.keyForActiveTabAndDimension, {
            pageNumber: this.pageNumber
          })
          .then(({ campaigns: filteredCampaigns }) => {
            this.campaignQueue.addLast(filteredCampaigns);
            this.loading = false;
            this.requestInProgress = false;
          });
      })
      .catch((error) => {
        console.error('Error occured while fetching campaigns', error);
        this.loading = false;
      })
      .finally(() => {
        this.requestInProgress = false;
      });
  }

  private addActionsColumn() {
    if (this.columns.findIndex((column) => column.field === 'actions') < 0) {
      this.columns.push({
        field: 'actions',
        type: 'actions',
        header: '',
        width: '4rem !important'
      });
    }
  }

  private updateTaxonomyColumnFields() {
    if (this.activeTab === TaxonomyTab.underProcessing) {
      this.columns = cloneDeep(this.columns).map((column) => {
        if (['tactic', 'channel', 'segment'].includes(column.field)) {
          column.field = camelCase(`operational_${column.field}`);
        }
        return column;
      });
    }
  }

  private async prepareCampaignTableColumnsByDimension(): Promise<void> {
    try {
      this.columns = uniqBy(
        await this.campaignViewService.getDimensionsByDatasourceOrGroupByCampaignDimension(
          this.filterContext.groupByDimension as TaxonomyDimension,
          this.campaignDimensionInfo?.dimension as string,
          this.isAiRecommendedDataRequested
            ? TaxonomyFeature.AI
            : this.activeTab
        ),
        'field'
      );
      CampaignTableService.updateModifiedByColumnForAi(
        this.viewFor,
        this.columns
      );

      this.addActionsColumn();
      if (this.activeTab !== TaxonomyTab.unmapped) {
        TaxonomyHelper.handleSpendColumnVisibility(this.columns);
      }
      this.updateTaxonomyColumnFields();
      this.actualColumns = cloneDeep(this.columns);
      this.prepareAndCollateWithFilterContextTableColumns();
    } catch (error) {
      console.error(
        'Error occured while preparing campaign table columns',
        error
      );
    }
  }

  public draftCampaignMapping(): void {
    this.campaignsGlobalLoaderService.showGlobalLoader(
      'Drafting campaign mapping',
      'Please wait while we draft the campaign mapping',
      ViewType.success
    );
  }

  public async selectOrunselectAllCampaigns(select: boolean): Promise<void> {
    this.selectionInProgress = true;
    if (select) {
      this.tableDataService.selectAll(this.keyForActiveTabAndDimension);

      this.campaignQueue.queue = this.campaignQueue.queue.map(
        (campaignInQueue: IVCampaign) => ({
          ...campaignInQueue,
          isSelected: true
        })
      );

      this.selectedCampaigns = this.campaignQueue.queue;
      this.selectionInProgress = false;

      this.showUpdateMappingOverlay = true;
    } else {
      this.tableDataService.unselectAll(this.keyForActiveTabAndDimension);

      this.campaignQueue.queue.map((campaignInQueue) => ({
        ...campaignInQueue,
        isSelected: false
      }));

      this.selectedCampaigns = [];
      this.selectionInProgress = false;

      this.showUpdateMappingOverlay =
        this.campaignQueue.queue.filter(
          (campaignInQueue) => campaignInQueue.isSelected
        ).length > 0;
    }

    this.selectedCampaigns = uniq(this.selectedCampaigns);
    this.allCampaignsSelected =
      this.selectedCampaigns.length > 0 &&
      this.selectedCampaigns.length ===
        (this.campaignDimensionInfo?.count || 0);
  }

  public getSelectedCampaignCount() {
    let campaignsCount = this.selectedCampaigns.length;
    if (this.allCampaignsSelected) {
      campaignsCount = this.isFilterContextApplied()
        ? this.campaignQueue.queue.length || 0
        : this.campaignDimensionInfo?.count || 0;
    }
    return campaignsCount;
  }

  public prepareReviewNavigationButtonsWithUndo() {
    this.reviewNavigationButtons =
      CampaignsViewConstants.reviewCloseNavigationButtons;
  }

  public isFilterContextApplied() {
    return (
      Object.entries(
        this.filterStateByTab[this.getColumnFilterStateKey()]?.filterCriteria ||
          {}
      ).filter(
        ([dimension, dimensionValue]) =>
          !isEmpty(dimension) &&
          (!isEmpty(dimensionValue?.contain) ||
            !isEmpty(dimensionValue?.exclude))
      ).length > 0
    );
  }

  private static updateModifiedByColumnForAi(
    viewFor: TaxonomyFeature,
    campaignViewColumns: IPTableColumnInfo[]
  ): void {
    if (viewFor !== TaxonomyFeature.AI) {
      return;
    }
    const modifiedByColumnIndex = campaignViewColumns.findIndex(
      (column) => column.field === Constants.modifiedByColumn.field
    );
    if (modifiedByColumnIndex > -1) {
      campaignViewColumns[modifiedByColumnIndex] = Constants.mappedByColumn;
    }
  }

  public changeActionStep(step: 'choice' | 'override') {
    this.actionStep = step;
    if (step === 'choice') {
      this.selectedCampaigns = [];
      this.allCampaignsSelected = false;
      this.selectedCampaignForAction = null;
      this.campaignQueue.queue.forEach(
        (campaign) => (campaign.isSelected = false)
      );
    }
  }

  public setSelectedActionCampaignToOverride() {
    this.selectedCampaigns = [this.selectedCampaignForAction as IVCampaign];
  }

  public prepareAndCollateWithFilterContextTableColumns() {
    if (isEmpty(this.filterContext.tableColumns)) {
      this.columns = this.actualColumns;
      return;
    }

    this.columns = this.actualColumns.filter(
      (column) =>
        this.filterContext.tableColumns?.includes(column.field) &&
        column.header !== ''
    );
  }
}
